import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import { getSize } from '../utils';
import {
  CompRef,
  CompLayout,
  ComponentLayoutObject,
  Point,
  CompStructure,
} from '@wix/document-services-types';
import { Item } from '@wix/editor-platform-host-integration-apis';

const TOKEN = 'unified-components-installation';

// TODO: [EP-4951] Add support for size according to preset breakpoint
const createLayoutAccordingToEditorType = ({
  width,
  height,
  x,
  y,
  docked,
}: CompLayout) => {
  if (window.commonConfig?.host?.toLowerCase() === 'studio') {
    // responsive layout
    const layouts: Partial<ComponentLayoutObject> = {
      componentLayout: {
        type: 'ComponentLayout',
        height: {
          type: 'auto',
        },
        minHeight: {
          type: 'px',
          value: height,
        },
        width: {
          type: 'px',
          value: width,
        },
        maxWidth: {
          type: 'percentage',
          value: 100,
        },
      },
      itemLayout: {
        id: 'itemLayout',
        type: 'GridItemLayout',
        gridArea: {
          rowStart: 1,
          rowEnd: 2,
          columnStart: 1,
          columnEnd: 2,
        },
        margins: {
          left: {
            type: 'percentage',
            value: 0,
          },
          right: {
            type: 'percentage',
            value: 0,
          },
          top: {
            type: 'percentage',
            value: 0,
          },
          bottom: {
            type: 'percentage',
            value: 0,
          },
        },
        justifySelf: docked ? 'stretch' : 'center',
        alignSelf: 'center',
      },
      type: 'SingleLayoutData',
    };
    return { layouts };
  }

  // classic layout
  const layout: CompLayout = {
    fixedPosition: true,
    x,
    y,
    width,
    height,
    ...(docked ? { docked } : {}),
  };

  return {
    layout,
  };
};

export function getCustomElementStructure(
  appDefinitionId: string,
  widgetId: string,
  size: Item['size'],
  position: Point,
): CompStructure {
  return {
    componentType: 'wixui.CustomElementComponent',
    data: {
      appDefinitionId,
      widgetId,
      type: 'CustomElement',
      shouldReload: false,
      initialAttributes: '{}',
    },
    ...createLayoutAccordingToEditorType({
      ...position,
      ...size!,
    }),
  };
}

export async function addCustomElement(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  { presetId, position = { x: 0, y: 0 } }: AddToStageData,
): Promise<CompRef> {
  // TODO: [EP-4952] Remove deprecatedSize usage after migration to presets is completed
  const { widgetId, size: deprecatedSize, presets } = widgetDefinition;
  // TODO: [EP-4736] Use default preset ID from installation settings instead of first preset in list
  //  Replace presets?.[0]?.id With widgetDefinition.installation.presetId as the fallback/default value
  const selectedPresetId = presetId ?? presets?.[0]?.id;
  const selectedPreset = (presets ?? []).find(
    (preset) => preset.id === selectedPresetId,
  );
  const size = selectedPreset?.breakpoints?.large?.size ?? deprecatedSize;

  function stretchIfNeeded(compRef: CompRef, reject: (reason?: any) => void) {
    if (size?.width?.stretchByDefault) {
      context.platformApiMethods.document.components
        .setFullWidth(appData, TOKEN, {
          componentRef: compRef,
          fullWidth: true,
        })
        .catch((error: Error) => {
          reject(
            createAddUnifiedComponentsError(
              ErrorCode.setWidgetFullWidthFailed,
              'Could not set widget to full width',
            )
              .withUserComponentId(widgetId, 'unified-widget')
              .withParentError(error),
          );
        });
    }
  }

  return new Promise<CompRef>((resolve, reject) => {
    context.platformApiMethods.document.components
      .add(appData, TOKEN, {
        componentDefinition: getCustomElementStructure(
          appData.appDefinitionId,
          widgetId,
          getSize(size),
          position,
        ),
        pageRef: containerRef,
      })
      .then((compRef: CompRef) => {
        context.documentServices.waitForChangesApplied(() => {
          stretchIfNeeded(compRef, reject);
          resolve(compRef);
        });
      })
      .catch((error: Error) => {
        reject(
          createAddUnifiedComponentsError(
            ErrorCode.addingCustomElementWidgetFailed,
            'Could not add custom element widget',
          )
            .withUserComponentId(widgetId, 'unified-widget')
            .withParentError(error),
        );
      });
  });
}
